/**
 * ObraSummary — Componente del Resumen Ejecutivo (Fase 6).
 *
 * - Muestra la última `obraAnalysis` del proyecto.
 * - Botón "Generar/Regenerar" → encola el job analyze-project.
 * - Reactivo a eventos `analyze:progress` que llegan por WebSocket
 *   (el padre los pasa vía prop `liveProgress`).
 * - Botón "Exportar PDF" → abre la versión print en nueva pestaña.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  ProgressBar,
  Spinner,
  Tag,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useCanMutateGeneric } from '../stores/quotaGuards.js';
import { useIsEcosoftStaff } from '../stores/authStore.js';
import { mensajeDeFalloIa, motivoDeErrorDeIa } from '@supernova/shared';
import { refreshUsageSoon } from '../stores/usageStore.js';
import {
  ArrowSync24Regular,
  DocumentBulletList24Regular,
  Print24Regular,
  Sparkle24Regular,
} from '@fluentui/react-icons';
import {
  getSummary,
  regenerateSummary,
  stopSummary,
  summaryPrintUrl,
  type ProjectSummary,
  type ObraAnalysis,
} from '../api/projectAnalysis.js';
import { getObraContext, deleteObraContext } from '../api/apuIa.js';
import type { AnalyzeProgressEvent } from '../api/projectStream.js';

interface Props {
  projectId: string;
  /** Último evento analyze:progress recibido por WS (opcional). */
  liveProgress?: AnalyzeProgressEvent['data'] | null;
  /**
   * Estado autoritativo del proyecto desde el servidor.
   * Sirve para arrancar UI sincronizada incluso sin WS.
   */
  serverStatus?: 'idle' | 'analyzing' | 'ready' | 'failed';
  /** Si es false, deshabilita "Generar/Regenerar" (proyecto de ejemplo/solo lectura). */
  canRegenerate?: boolean;
  /** El contexto de proyecto se reinició (al "rehacer" en regeneración) → el
   *  padre debe refrescar el semáforo del botón "Contexto de proyecto". */
  onContextReset?: () => void;
  /** Dispara la regeneración automáticamente al montar (una sola vez). Se usa
   *  cuando el usuario llega desde el Contexto con "Generar resumen". */
  autoGenerate?: boolean;
  /** El proyecto aún procesa documentos (parseo/indexación) → no se puede
   *  generar el resumen todavía (saldría incompleto). Deshabilita el botón. */
  processing?: boolean;
  /** El proyecto solo tiene planos u otros archivos sin texto (0 chunks): no hay
   *  de qué armar un resumen. Deshabilita el botón y explica que falta el pliego. */
  sinTexto?: boolean;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-l)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-s)',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-xs)',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  actions: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
  },
  emptyHero: {
    border: '1px dashed var(--border-subtle)',
    borderRadius: 'var(--radius-l)',
    padding: 'var(--space-xl)',
    textAlign: 'center',
    color: 'var(--text-tertiary)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-m)',
    alignItems: 'center',
  },
  card: {
    background: 'var(--bg-elev)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-l)',
    padding: 'var(--space-l)',
  },
  field: {
    display: 'grid',
    gridTemplateColumns: '110px 1fr',
    gap: 'var(--space-s)',
    fontSize: 'var(--fs-200)',
    padding: '4px 0',
  },
  fieldKey: {
    color: 'var(--text-tertiary)',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-100)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  fieldVal: {
    color: 'var(--text-primary)',
    wordBreak: 'break-word',
  },
  null: { color: 'var(--text-tertiary)', fontStyle: 'italic' },
  resumen: {
    background: tokens.colorNeutralBackground2,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    padding: 'var(--space-m) var(--space-l)',
    borderRadius: 'var(--radius-m)',
    fontSize: 'var(--fs-300)',
    lineHeight: 1.5,
  },
  obsList: {
    margin: 0,
    paddingLeft: '20px',
    fontSize: 'var(--fs-200)',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  filesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '4px',
    fontSize: 'var(--fs-100)',
    fontFamily: 'var(--font-mono)',
  },
  filesRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0',
    color: 'var(--text-secondary)',
  },
  meta: {
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    fontFamily: 'var(--font-mono)',
    marginTop: 'var(--space-s)',
  },
  err: {
    color: 'var(--semantic-danger)',
    fontSize: 'var(--fs-200)',
    background: 'var(--bad-weak)',
    padding: 'var(--space-s)',
    borderRadius: 'var(--radius-s)',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-xs)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
  },
});

const STATUS_LABEL: Record<NonNullable<Props['serverStatus']>, string> = {
  idle: 'no generado',
  analyzing: 'generando',
  ready: 'listo',
  failed: 'falló',
};
const STATUS_COLOR: Record<NonNullable<Props['serverStatus']>, string> = {
  idle: 'var(--text-tertiary)',
  analyzing: 'var(--semantic-warning)',
  ready: 'var(--semantic-success)',
  failed: 'var(--semantic-danger)',
};

const CLASS_LABELS: Record<string, string> = {
  bases: 'Bases / Convocatoria',
  catalogo: 'Catálogo de Conceptos',
  contrato: 'Modelo de Contrato',
  anexo: 'Anexo',
  especificacion: 'Especificaciones',
  formato: 'Formato',
  plano: 'Plano',
  programa: 'Programa de Obra',
  estudio: 'Estudio Técnico',
  presupuesto: 'Presupuesto',
  matrices: 'Matrices de P.U.',
  insumos: 'Listado de Insumos',
  criterios: 'Criterios de Evaluación',
  instructivo: 'Instructivo',
  legal: 'Documento Legal',
  imagen: 'Imagen',
  software: 'Software',
  otro: 'Otro',
};

export function ObraSummary({ projectId, liveProgress, serverStatus, canRegenerate = true, onContextReset, autoGenerate, processing, sinTexto }: Props) {
  const styles = useStyles();
  const guard = useCanMutateGeneric();
  const isEcosoftStaff = useIsEcosoftStaff();
  const [summary, setSummary] = useState<ProjectSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Muestra el diálogo "¿rehacer el contexto?" cuando hay uno guardado (ajustado).
  const [askRehacer, setAskRehacer] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await getSummary(projectId);
      setSummary(s);
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  // Cuando llega "done" o "failed" por WS, refrescar para traer el JSON nuevo.
  useEffect(() => {
    if (!liveProgress) return;
    if (liveProgress.stage === 'done' || liveProgress.stage === 'failed') {
      void refresh();
      if (liveProgress.stage === 'done') refreshUsageSoon(); // el resumen consumió tokens → refrescar pill
    }
  }, [liveProgress, refresh]);

  // Llega desde el Contexto con "Generar resumen" → dispara la regeneración una
  // sola vez (pasa por el diálogo Rehacer/Mantener si hay contexto guardado).
  const autoTriggered = useRef(false);
  useEffect(() => {
    // Si aún procesa documentos, espera: cuando `processing` pase a false, dispara.
    // Si el proyecto no tiene texto (solo planos), NO se auto-genera: no hay resumen posible.
    if (autoGenerate && !processing && !sinTexto && !autoTriggered.current) {
      autoTriggered.current = true;
      void onRegenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate, processing]);

  // Corre la regeneración. Si `rehacerContexto`, primero reinicia el contexto de
  // proyecto guardado (queda "sin capturar" → semáforo amarillo → se re-infiere del
  // nuevo análisis). Si no, el contexto guardado se mantiene intacto (verde).
  async function doRegenerate(rehacerContexto: boolean) {
    setBusy(true);
    setErr(null);
    try {
      if (rehacerContexto) {
        await deleteObraContext(projectId).catch(() => { /* best-effort */ });
      }
      await regenerateSummary(projectId);
      // Optimista: marcar UI como analyzing para feedback inmediato.
      setSummary((prev) =>
        prev
          ? { ...prev, status: 'analyzing', error: null }
          : { projectId, status: 'analyzing', summaryAt: null, error: null, analysis: null },
      );
      if (rehacerContexto) onContextReset?.();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onRegenerate() {
    // Si hay un contexto de proyecto GUARDADO (ajustado, no deducido), preguntar
    // si rehacerlo con el nuevo análisis (queda amarillo) o mantenerlo (verde).
    // Sin contexto (o solo deducido) → regenerar directo.
    try {
      const ctx = await getObraContext(projectId);
      if (ctx && ctx.source !== 'deduced') {
        setAskRehacer(true);
        return;
      }
    } catch { /* 404 = sin contexto guardado → regenerar directo */ }
    await doRegenerate(false);
  }

  const status: NonNullable<Props['serverStatus']> =
    (liveProgress?.summaryStatus as any) ?? summary?.status ?? serverStatus ?? 'idle';
  const isAnalyzing = status === 'analyzing';
  // DETENER el resumen (reporte de dirección). Con confirmación, como todos los Altos: es
  // una acción que no se deshace, y los segundos que tarda el usuario en confirmar son los
  // que el worker usa para cerrar lo que está haciendo.
  const [confirmarAlto, setConfirmarAlto] = useState(false);
  const [deteniendo, setDeteniendo] = useState(false);
  async function onStop() {
    setConfirmarAlto(false);
    setDeteniendo(true);
    try {
      await stopSummary(projectId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo detener');
      setDeteniendo(false);
    }
  }
  useEffect(() => { if (deteniendo && !isAnalyzing) setDeteniendo(false); }, [isAnalyzing, deteniendo]);
  const analysis: ObraAnalysis | null = summary?.analysis ?? null;
  // Motivo por el que el botón está bloqueado (para el tooltip). Procesar
  // documentos tiene prioridad sobre el guard de rol/cuota.
  const blockedMsg = processing
    ? 'Espera a que terminen de procesarse los documentos'
    : sinTexto
      ? 'Este proyecto solo tiene planos, sin texto. Sube el pliego (bases o especificaciones) para generar el resumen.'
      : !guard.allowed
        ? (guard.message ?? 'Acción no permitida')
        : '';

  const stageLabel: Record<NonNullable<typeof liveProgress>['stage'], string> = {
    collect: 'Cargando archivos…',
    ai: 'Analizando con inteligencia artificial…',
    done: '¡Listo!',
    failed: 'Falló',
    stopped: 'Se detuvo',
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.title}>
          <Sparkle24Regular fontSize={14} /> Resumen ejecutivo
        </span>
        {/* El pill "listo" parecía botón y no aporta cuando todo está bien
            (reunión 2026-07-02); solo se muestra en estados informativos. */}
        {status !== 'ready' && (
          <Tag
            size="extra-small"
            appearance="filled"
            style={{ color: STATUS_COLOR[status], borderColor: STATUS_COLOR[status] }}
          >
            {STATUS_LABEL[status]}
          </Tag>
        )}
      </div>

      {/* Confirmación para DETENER el resumen. Regla: todo Alto se confirma — no se deshace,
          y esa pausa le da al worker unos segundos para cerrar lo que está haciendo. */}
      <Dialog open={confirmarAlto} onOpenChange={(_, d) => { if (!d.open) setConfirmarAlto(false); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>¿Detener el resumen de la obra?</DialogTitle>
            <DialogContent>
              <p style={{ margin: 0 }}>
                No se va a guardar nada a medias: un resumen incompleto no sirve y puede
                confundir. {analysis ? 'El resumen que ya tenías se conserva tal como está.' : 'El proyecto queda como estaba.'}
                {' '}Puedes volver a generarlo cuando quieras.
              </p>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmarAlto(false)}>Seguir trabajando</Button>
              <Button appearance="primary" onClick={() => void onStop()}>Sí, detener</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Acciones */}
      <div className={styles.actions}>
        <Tooltip
          content={blockedMsg}
          relationship="label"
          visible={blockedMsg ? undefined : false}
        >
          <Button
            appearance="primary"
            icon={isAnalyzing ? <Spinner size="extra-tiny" /> : <ArrowSync24Regular />}
            size="small"
            disabled={busy || isAnalyzing || !guard.allowed || !canRegenerate || !!processing || !!sinTexto}
            onClick={() => void onRegenerate()}
            data-demo="generar-resumen"
          >
            {isAnalyzing
              ? 'Generando…'
              : analysis
                ? 'Regenerar'
                : 'Generar resumen'}
          </Button>
        </Tooltip>
        {isAnalyzing && (
          <Button
            appearance="subtle"
            size="small"
            disabled={deteniendo}
            onClick={() => setConfirmarAlto(true)}
            title="Detener la generación del resumen ejecutivo de la obra"
          >
            {/* Nombra el proceso: al lado puede estar el Alto de la lectura de documentos
                y dos "Detener" iguales no se distinguen (pedido de Sebastián, 2026-08-12). */}
            {deteniendo ? 'Deteniendo el resumen…' : 'Detener el resumen'}
          </Button>
        )}
        {analysis && (
          <Button
            appearance="subtle"
            icon={<Print24Regular />}
            size="small"
            as="a"
            href={summaryPrintUrl(projectId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Exportar PDF
          </Button>
        )}
      </div>

      {/* Progreso live */}
      {isAnalyzing && liveProgress && (
        <div>
          <div className={styles.status}>
            <Spinner size="tiny" />
            {/* Con el Alto pedido, la leyenda dice QUÉ se está deteniendo: el worker sigue
                un rato cerrando lo que tiene en la mano y sin esto parecía que no pasó nada. */}
            <span>
              {deteniendo
                ? 'Deteniendo el resumen de la obra…'
                : (liveProgress.message ?? stageLabel[liveProgress.stage])}
            </span>
          </div>
          <ProgressBar />
          {liveProgress.detectedEntity && (
            <div className={styles.meta}>
              detectado: {liveProgress.detectedEntity}
              {liveProgress.detectedLicitNum ? ` · ${liveProgress.detectedLicitNum}` : ''}
            </div>
          )}
        </div>
      )}

      {/* Errores */}
      {err && <div className={styles.err}>{err}</div>}
      {summary?.error && status === 'failed' && (
        <div className={styles.err}>{summary.error}</div>
      )}

      {/* Estado vacío */}
      {!loading && !analysis && !isAnalyzing && (
        <div className={styles.emptyHero}>
          <DocumentBulletList24Regular fontSize={32} />
          <div>
            <div style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
              {sinTexto ? 'No se puede generar el resumen' : 'Aún no hay resumen ejecutivo'}
            </div>
            <div style={{ fontSize: 'var(--fs-100)' }}>
              {sinTexto
                ? 'Este proyecto solo tiene planos, sin texto. Sube el pliego (bases o especificaciones) para generar el resumen.'
                : 'Genera el resumen para extraer entidad, número de licitación, fechas, garantía y observaciones clave.'}
            </div>
          </div>
        </div>
      )}

      {/* Análisis */}
      {analysis && (
        <>
          <div className={styles.card}>
            <Field k="Entidad" v={analysis.detectedEntity} />
            <Field k="Licitación" v={analysis.detectedLicitNum} mono />
            <Field k="Tipo" v={analysis.ai?.tipoLicitacion ?? null} />
            <Field k="Objeto" v={analysis.detectedObjeto} />
            {analysis.ai?.fechaApertura && <Field k="Apertura" v={analysis.ai.fechaApertura} />}
            {analysis.ai?.fechaVisita && <Field k="Visita" v={analysis.ai.fechaVisita} />}
            {analysis.ai?.plazoEjecucion && <Field k="Plazo" v={analysis.ai.plazoEjecucion} />}
            {analysis.ai?.montoGarantia && <Field k="Garantía" v={analysis.ai.montoGarantia} />}
            {analysis.ai?.moneda && <Field k="Moneda" v={analysis.ai.moneda} />}
          </div>

          {analysis.ai?.resumen && (
            <div className={styles.resumen}>{analysis.ai.resumen}</div>
          )}

          {analysis.ai?.observaciones && analysis.ai.observaciones.length > 0 && (
            <div>
              <div className={styles.title} style={{ marginBottom: 'var(--space-xs)' }}>
                Observaciones
              </div>
              <ul className={styles.obsList}>
                {analysis.ai.observaciones.map((o, i) => (
                  <li key={i}>{o}</li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <div className={styles.title} style={{ marginBottom: 'var(--space-xs)' }}>
              Archivos por tipo
            </div>
            <div className={styles.filesGrid}>
              {Object.entries(analysis.filesByClass)
                .sort((a, b) => b[1] - a[1])
                .map(([cls, n]) => (
                  <div key={cls} className={styles.filesRow}>
                    <span>{CLASS_LABELS[cls] ?? cls}</span>
                    <span style={{ color: 'var(--text-primary)' }}>{n}</span>
                  </div>
                ))}
            </div>
          </div>

          {analysis.meta.aiError && (
            <div className={styles.meta} style={{ color: 'var(--semantic-danger)' }}>
              {/* Client-safe: el aviso único de @supernova/shared. El error crudo («AIClient.chat
                  timeout», «Sin API key para provider=…») solo para staff Ecosoft. */}
              ⚠ {mensajeDeFalloIa(motivoDeErrorDeIa(analysis.meta.aiError))}
              {' '}Los datos de arriba se detectaron sin IA.
              {isEcosoftStaff && <> (Detalle técnico, solo Ecosoft: {analysis.meta.aiError})</>}
            </div>
          )}
        </>
      )}

      <Dialog open={askRehacer} onOpenChange={(_, d) => setAskRehacer(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Regenerar resumen</DialogTitle>
            <DialogContent>
              Ya tienes un <strong>contexto de proyecto guardado</strong>. Al regenerar el
              resumen, ¿quieres rehacer también el contexto con el nuevo análisis o mantener el
              que ajustaste?
              <ul style={{ margin: '10px 0 0', paddingLeft: 18, lineHeight: 1.5 }}>
                <li><strong>Rehacer:</strong> se re-infiere del nuevo análisis y queda en amarillo para que lo revises.</li>
                <li><strong>Mantener:</strong> tu contexto se queda como está (verde), sin tocar nada.</li>
              </ul>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setAskRehacer(false)}>Cancelar</Button>
              <Button
                appearance="secondary"
                onClick={() => { setAskRehacer(false); void doRegenerate(false); }}
              >
                Mantener contexto
              </Button>
              <Button
                appearance="primary"
                onClick={() => { setAskRehacer(false); void doRegenerate(true); }}
              >
                Rehacer contexto
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

function Field({
  k,
  v,
  mono,
}: {
  k: string;
  v: string | null | undefined;
  mono?: boolean;
}) {
  const styles = useStyles();
  return (
    <div className={styles.field}>
      <span className={styles.fieldKey}>{k}</span>
      <span
        className={v ? styles.fieldVal : styles.null}
        style={mono ? { fontFamily: 'var(--font-mono)' } : undefined}
      >
        {v ?? '(no detectado)'}
      </span>
    </div>
  );
}
